/**
 * Letter Feedback Utility
 * Provides Wordle-style letter matching feedback for word attempts
 */

/**
 * Analyze a submitted word against the target word and return letter-by-letter feedback
 * Similar to Wordle, this respects letter frequency in the target word
 * 
 * @param {string} submittedWord - The word the player submitted
 * @param {string} targetWord - The target word to match
 * @returns {Array<{letter: string, status: string}>} Array of letter feedback objects
 * 
 * Status values:
 * - 'correct': Letter exists in the target word (green in Wordle)
 * - 'absent': Letter does not exist in the target word (gray in Wordle)
 * 
 * Note: We're simplifying compared to Wordle - we only mark if a letter exists in the target,
 * not if it's in the correct position, since WordRive is about finding any valid word from letters.
 */
export function analyzeLetterFeedback(submittedWord, targetWord) {
  if (!submittedWord || !targetWord) {
    return [];
  }

  // Normalize both words to uppercase for comparison
  const submitted = submittedWord.toUpperCase();
  const target = targetWord.toUpperCase();

  // Count letter frequency in target word
  const targetLetterCounts = {};
  for (const letter of target) {
    targetLetterCounts[letter] = (targetLetterCounts[letter] || 0) + 1;
  }

  // Track how many of each letter we've already marked as 'correct'
  const usedLetterCounts = {};

  // First pass: analyze each letter in the submitted word
  const feedback = [];
  
  for (let i = 0; i < submitted.length; i++) {
    const letter = submitted[i];
    
    // Check if this letter exists in the target word
    const targetCount = targetLetterCounts[letter] || 0;
    const usedCount = usedLetterCounts[letter] || 0;

    let status = 'absent';

    // If the letter exists in target and we haven't used up all instances
    if (targetCount > 0 && usedCount < targetCount) {
      status = 'correct';
      usedLetterCounts[letter] = usedCount + 1;
    }

    feedback.push({
      letter: letter,
      status: status
    });
  }

  return feedback;
}

/**
 * Get a summary of which unique letters from the submitted word are in the target
 * Useful for displaying "Letters found: A, E, T" type messages
 * 
 * @param {string} submittedWord - The word the player submitted
 * @param {string} targetWord - The target word to match
 * @returns {Array<string>} Array of unique letters that exist in target
 */
export function getMatchingLetters(submittedWord, targetWord) {
  const feedback = analyzeLetterFeedback(submittedWord, targetWord);
  const matchingLetters = new Set();

  feedback.forEach(item => {
    if (item.status === 'correct') {
      matchingLetters.add(item.letter);
    }
  });

  return Array.from(matchingLetters).sort();
}

/**
 * Calculate how many letters from the submitted word exist in the target word
 * 
 * @param {string} submittedWord - The word the player submitted
 * @param {string} targetWord - The target word to match
 * @returns {number} Count of matching letters (respecting frequency)
 */
export function countMatchingLetters(submittedWord, targetWord) {
  const feedback = analyzeLetterFeedback(submittedWord, targetWord);
  return feedback.filter(item => item.status === 'correct').length;
}

